import { useCallback, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { showApolloErrorToUser, useConfirm, Toast } from '@pletly/ui';
import { getName } from '@pletly/shared';
import { useConnections } from '%context/Connections';
import { GetProtegesDocument } from '%context/Connections/__generated__/getProteges.generated';
import { DELETE_FRIEND } from '%components/FriendsList/deleteFriend.graphql';
import {
	DeleteFriend,
	DeleteFriendVariables,
} from '%components/FriendsList/__generated__/deleteFriend';
import { GetOwnedGroupsDocument } from '%components/GroupsList/__generated__/getOwnedGroups.generated';
import {
	useInvitations,
	useRejectInvitation,
	useAcceptInvitation,
	INVITES_QUERIES_TO_REFETCH,
} from '%context/Invitations';

import { CONNECTIONS_MANAGE_ENABLED } from '%constants/connections';
import { DataType, DataItem, Item, Section, PlaceholderItem, PlaceholderType } from './types';
import { useAcceptImportToOrganisationRequestMutation } from './__generated__/acceptImportRequest.mutation.generated';
import { useRejectImportToOrganisationRequestMutation } from './__generated__/rejectImportRequest.mutation.generated';

export const useFriendsAndIvnites = () => {
	const { t } = useTranslation();

	const { invitations, refetch: refreshInvites, loading: invitesLoading } = useInvitations();
	const rejectInvitation = useRejectInvitation();
	const acceptInvitation = useAcceptInvitation();
	const [acceptImportRequest] = useAcceptImportToOrganisationRequestMutation();
	const handleAcceptImportRequest = useCallback(
		async (inviteId: string) => {
			try {
				await acceptImportRequest({
					variables: {
						inviteId,
					},
					refetchQueries: INVITES_QUERIES_TO_REFETCH,
				});

				return Toast.show({
					type: 'success',
					text1: t('connections.request-accepted'),
					text2: t('connections.care-receiver-is-now-part-of-the-organisation'),
				});
			} catch (error) {
				showApolloErrorToUser(error as Error);
			}
		},
		[t, acceptImportRequest],
	);
	const [rejectImportRequest] = useRejectImportToOrganisationRequestMutation();
	const handleRejectImportRequest = useCallback(
		async (inviteId: string) => {
			try {
				await rejectImportRequest({
					variables: {
						inviteId,
					},
					refetchQueries: INVITES_QUERIES_TO_REFETCH,
				});

				return Toast.show({
					type: 'success',
					text1: t('connections.request-rejected'),
				});
			} catch (error) {
				showApolloErrorToUser(error as Error);
			}
		},
		[t, rejectImportRequest],
	);

	const { confirm } = useConfirm();
	const [deleteFriend] = useMutation<DeleteFriend, DeleteFriendVariables>(DELETE_FRIEND, {
		refetchQueries: [GetProtegesDocument, GetOwnedGroupsDocument],
	});
	const {
		proteges,
		temporaryProteges,
		loading: friendsLoading,
		refetch: refetchFriends,
	} = useConnections();

	const friends = temporaryProteges.concat(
		proteges.map((protege) => ({
			user: protege,
			endDate: null,
		})),
	);

	const handleDeleteFriend = async (uuid: string, name: string) => {
		const confirmation = await confirm(
			t('connections.remove-name', { name }),
			`${t('connections.are-you-sure-you-want-to-remove-name-from-connections', { name })}`,
		);
		if (!confirmation) return;

		return deleteFriend({ variables: { uuid } });
	};

	const handleReject = (id: string) => {
		return rejectInvitation({ variables: { id } });
	};

	const listItems = useMemo(() => {
		if (!invitations) {
			return null;
		}

		const { friendshipRequestsReceived, superPalOf } = invitations.currentUser;
		const protegesInvites = superPalOf
			.map((protege) =>
				protege.friendshipRequestsReceived.map((invite) => ({ ...invite, toUser: protege })),
			)
			.flat();
		const friendsSection = {
			title: t('connections.my-connections'),
			data: friends,
			type: DataType.Friends,
		};
		const sections = CONNECTIONS_MANAGE_ENABLED
			? [
					{
						title: t('connections.invitations-to-become-caregiver'),
						data: friendshipRequestsReceived,
						type: DataType.ReceivedInvites,
					},
					{
						title: t('connections.invites-of-your-proteges'),
						data: protegesInvites,
						type: DataType.CareReceiverInvites,
					},
					{
						title: t('connections.organisation-requests'),
						data: superPalOf
							.map((protege) =>
								protege.addToOrganisationRequestsReceived.map((invite) => ({
									...invite,
									fromUser: {
										...protege,
										firstName: null,
										lastName: null,
										username: t('connections.transfer-name-to-organisation', {
											name: getName(protege),
											organisation: invite.organisation.name,
										}),
									},
									toUser: { username: invite.organisation.name },
								})),
							)
							.flat(),
						type: DataType.CareReceiverImportToOrganisationInvites,
					},
					friendsSection,
				]
			: [friendsSection];

		return sections.map(createSection).flat();
	}, [invitations, friends, t]);

	return {
		acceptInvitation,
		handleReject,
		handleDeleteFriend,
		friendsLoading,
		invitesLoading,
		refetchFriends,
		refreshInvites,
		listItems,
		handleAcceptImportRequest,
		handleRejectImportRequest,
	};
};

function createSection<Type extends DataItem>({
	title,
	data: sectionData,
	type,
}: Section<Type>): Item[] {
	if (sectionData.length <= 0) {
		return [];
	}

	const itemsWithDividers = sectionData
		.map((item) => [
			{ data: item, type } as DataItem,
			{ type: PlaceholderType.Divider } as PlaceholderItem,
		])
		.flat();

	return [{ title, type: PlaceholderType.Title }, ...itemsWithDividers];
}
